import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert, Button, CloseIcon, HStack, IconButton, Progress, Text, VStack } from 'native-base';

import { IStore } from '../../redux/reducers';
import * as UpdateActions from '../../redux/actions/updateAction';
import { isElectron } from '../../utils/miscUtils';

const CUDA_BANNER_DISMISSED_VERSION_KEY = '@cuda_banner_dismissed_version';

function isWindowsElectron(): boolean {
  try {
    const platform = (typeof window !== 'undefined' && (window as any)?.electron?.platform) || null;
    return isElectron() && platform === 'win32';
  } catch {
    return false;
  }
}

export default function CudaRuntimeBanner() {
  const dispatch = useDispatch();

  const availableUpdates = useSelector((store: IStore) => store.update.availableUpdates);
  const downloadProgress = useSelector((store: IStore) => store.update.downloadProgress);
  const updateStatus = useSelector((store: IStore) => store.update.updateStatus);

  const cudaUpdate = useMemo(() => availableUpdates.find((u) => u.type === 'ml_runtime_cuda'), [availableUpdates]);
  const cudaProgress = (downloadProgress as any)?.ml_runtime_cuda;

  const [dismissedForVersion, setDismissedForVersion] = useState<string | null>(null);
  const [cudaInstalled, setCudaInstalled] = useState<boolean>(false);
  const [cudaCompatible, setCudaCompatible] = useState<boolean | null>(null);
  const [gpuNames, setGpuNames] = useState<string[]>([]);

  const candidateVersion = String(cudaUpdate?.version || '').trim();
  const isReady = !!candidateVersion && cudaProgress?.status === 'ready';
  const isDownloading = !!candidateVersion && cudaProgress?.status === 'downloading';
  const isInstalling = updateStatus === 'installing';

  // Load dismissal + runtime state only when we have a ready/downloaded update.
  useEffect(() => {
    if (!isWindowsElectron()) return;
    if (!candidateVersion) return;
    if (!isReady && !isDownloading) return;

    let cancelled = false;
    const run = async () => {
      try {
        const dismissed = await AsyncStorage.getItem(CUDA_BANNER_DISMISSED_VERSION_KEY);
        if (!cancelled) setDismissedForVersion(dismissed ? String(dismissed).trim() : null);
      } catch {
        if (!cancelled) setDismissedForVersion(null);
      }

      try {
        const electronAPI = (window as any)?.electronAPI;
        if (electronAPI?.checkCudaCompatibility) {
          const compat = await electronAPI.checkCudaCompatibility();
          if (!cancelled) {
            setCudaCompatible(!!compat?.compatible);
            setGpuNames(Array.isArray(compat?.gpus) ? compat.gpus : []);
          }
        }
        if (electronAPI?.getMlRuntimeStatus) {
          const status = await electronAPI.getMlRuntimeStatus();
          if (!cancelled) setCudaInstalled(!!status?.cudaInstalled);
        }
      } catch {
        // best-effort only
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [candidateVersion, isReady, isDownloading]);

  const isDismissed = dismissedForVersion === candidateVersion;
  const showBanner =
    isWindowsElectron()
    && !!candidateVersion
    && isReady
    && cudaCompatible === true
    && !cudaInstalled
    && !isDismissed;

  if (!showBanner) return null;

  const onApply = () => {
    dispatch(UpdateActions.applyUpdates.request());
  };

  const onDismiss = async () => {
    try {
      await AsyncStorage.setItem(CUDA_BANNER_DISMISSED_VERSION_KEY, candidateVersion);
    } catch {
      // ignore
    }
    setDismissedForVersion(candidateVersion);
  };

  const gpuSummary = gpuNames.length ? ` Detected GPU(s): ${gpuNames.join(', ')}` : '';

  return (
    <Alert w="100%" status="info" variant="left-accent">
      <VStack space={2} flexShrink={1} w="100%">
        <HStack space={2} alignItems="center" justifyContent="space-between">
          <HStack space={2} flexShrink={1} alignItems="center">
            <Alert.Icon />
            <Text flexShrink={1} fontSize="sm" color="coolGray.800">
              GPU acceleration is ready to install (CUDA runtime v{candidateVersion} downloaded).{gpuSummary}
            </Text>
          </HStack>
          <IconButton
            variant="unstyled"
            onPress={onDismiss}
            _focus={{ borderWidth: 0 }}
            icon={<CloseIcon size="3" />}
            _icon={{ color: 'coolGray.600' }}
          />
        </HStack>

        {isDownloading ? (
          <VStack space={1}>
            <Text fontSize="xs" color="coolGray.600">
              Downloading CUDA runtime… {cudaProgress?.progress ?? 0}%
            </Text>
            <Progress value={cudaProgress?.progress ?? 0} />
          </VStack>
        ) : null}

        <HStack space={2} justifyContent="flex-end">
          <Button size="sm" variant="outline" onPress={onDismiss} isDisabled={isInstalling}>
            Dismiss
          </Button>
          <Button size="sm" colorScheme="blue" onPress={onApply} isLoading={isInstalling}>
            Apply
          </Button>
        </HStack>
      </VStack>
    </Alert>
  );
}
